import {
	Alert,
	Anchor,
	Button,
	Group,
	Loader,
	Modal,
	Paper,
	Stack,
	Switch,
	Table,
	Text,
	Textarea,
	TextInput,
	Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import {
	getAdminUser,
	guestUserKeys,
	renameAdminUser,
	setAdminUserDiscountState,
} from "@/features/users/api/admin-users";
import type { AdminUserDiscount } from "@/features/users/api/types";
import { CardPreviewPanel } from "@/features/users/ui/card-preview-panel";
import { describeApiErrorSync } from "@/shared/api/error-message";
import { getMutationErrorMessage } from "@/shared/api/errors";

export function UserDetailPage() {
	const { t } = useTranslation();
	const { id = "" } = useParams();
	const queryClient = useQueryClient();
	const detailQuery = useQuery({
		queryKey: guestUserKeys.detail(id),
		queryFn: () => getAdminUser(id),
		enabled: Boolean(id),
	});

	const [name, setName] = useState<string | null>(null);
	const [surname, setSurname] = useState<string | null>(null);
	const [toggle, setToggle] = useState<AdminUserDiscount | null>(null);
	const [reason, setReason] = useState("");

	const user = detailQuery.data;
	const nameValue = name ?? user?.name ?? "";
	const surnameValue = surname ?? user?.surname ?? "";

	const renameMutation = useMutation({
		mutationFn: () =>
			renameAdminUser(id, {
				name: nameValue.trim(),
				surname: surnameValue.trim(),
			}),
		onSuccess: async (next) => {
			queryClient.setQueryData(guestUserKeys.detail(id), next);
			setName(null);
			setSurname(null);
			notifications.show({ color: "green", message: t("users.nameSaved") });
			await queryClient.invalidateQueries({ queryKey: guestUserKeys.all });
		},
		onError: async (error) => {
			const message = await getMutationErrorMessage(error);
			notifications.show({
				color: "red",
				message:
					message === "NAME_LOCKED_BY_APPROVAL"
						? t("users.nameLocked")
						: message,
			});
		},
	});

	const stateMutation = useMutation({
		mutationFn: () => {
			const current = toggle;
			if (!current) {
				return Promise.reject(new Error("missing discount"));
			}
			return setAdminUserDiscountState(id, {
				type: current.type,
				id: current.id ?? undefined,
				isActive: !current.isActive,
				reason: reason.trim() || undefined,
			});
		},
		onSuccess: async (next) => {
			queryClient.setQueryData(guestUserKeys.detail(id), next);
			setToggle(null);
			setReason("");
			notifications.show({
				color: "green",
				message: t("users.discountUpdated"),
			});
			await queryClient.invalidateQueries({ queryKey: guestUserKeys.all });
		},
		onError: async (error) => {
			const message = await getMutationErrorMessage(error);
			notifications.show({
				color: "red",
				message:
					message === "DISCOUNT_SLOT_TAKEN"
						? t("users.discountSlotTaken")
						: message,
			});
		},
	});

	if (detailQuery.isLoading) {
		return (
			<Group justify="center" py="xl">
				<Loader />
			</Group>
		);
	}

	if (detailQuery.error || !user) {
		return (
			<Alert color="red" title={t("users.loadError")}>
				<Text size="sm">{describeApiErrorSync(detailQuery.error)}</Text>
				<Anchor component={Link} to="/users">
					{t("users.back")}
				</Anchor>
			</Alert>
		);
	}

	return (
		<Stack gap="md">
			<div>
				<Anchor component={Link} to="/users" size="sm">
					{t("users.back")}
				</Anchor>
				<Title order={2}>
					{[user.name, user.surname].filter(Boolean).join(" ") || user.phone}
				</Title>
				<Text c="dimmed" size="sm">
					{user.phone}
					{user.email ? ` · ${user.email}` : ""}
				</Text>
			</div>

			<Paper p="md" radius="lg" withBorder maw={560}>
				<Stack>
					<Title order={4}>{t("users.profile")}</Title>
					<TextInput
						label={t("common.name")}
						value={nameValue}
						disabled={!user.canEditName}
						onChange={(event) => setName(event.currentTarget.value)}
					/>
					<TextInput
						label={t("users.surname")}
						value={surnameValue}
						disabled={!user.canEditName}
						onChange={(event) => setSurname(event.currentTarget.value)}
					/>
					{!user.canEditName ? (
						<Text size="sm" c="dimmed">
							{t("users.nameLocked")}
						</Text>
					) : (
						<Group justify="flex-end">
							<Button
								loading={renameMutation.isPending}
								disabled={!nameValue.trim() || !surnameValue.trim()}
								onClick={() => renameMutation.mutate()}
							>
								{t("users.saveName")}
							</Button>
						</Group>
					)}
				</Stack>
			</Paper>

			<CardPreviewPanel userId={user.id} />

			<Paper p="md" radius="lg" withBorder>
				<Stack>
					<Title order={4}>{t("users.discounts")}</Title>
					{user.discounts.length === 0 ? (
						<Text c="dimmed">{t("common.dash")}</Text>
					) : (
						<Table.ScrollContainer type="native" minWidth={480}>
							<Table>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>{t("users.discount")}</Table.Th>
										<Table.Th>{t("common.status")}</Table.Th>
										<Table.Th />
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{user.discounts.map((row) => (
										<Table.Tr key={`${row.type}-${row.id ?? "corp"}`}>
											<Table.Td>
												<Text fw={500}>{row.title}</Text>
												<Text size="sm" c="dimmed">
													{row.subtitle}
													{row.disableReason ? ` · ${row.disableReason}` : ""}
												</Text>
											</Table.Td>
											<Table.Td>
												{row.isActive
													? t("common.active")
													: t("common.inactive")}
											</Table.Td>
											<Table.Td>
												<Switch
													checked={row.isActive}
													onChange={() => {
														setToggle(row);
														setReason("");
													}}
												/>
											</Table.Td>
										</Table.Tr>
									))}
								</Table.Tbody>
							</Table>
						</Table.ScrollContainer>
					)}
				</Stack>
			</Paper>

			<Paper p="md" radius="lg" withBorder>
				<Stack>
					<Title order={4}>{t("users.family")}</Title>
					{user.family.members.length === 0 ? (
						<Text c="dimmed">{t("common.dash")}</Text>
					) : (
						user.family.members.map((member) => (
							<Text key={member.userId} size="sm">
								{[member.name, member.surname].filter(Boolean).join(" ") ||
									member.phone}{" "}
								— {member.role} ({member.status})
							</Text>
						))
					)}
				</Stack>
			</Paper>

			<Paper p="md" radius="lg" withBorder>
				<Stack>
					<Title order={4}>{t("users.requests")}</Title>
					{user.requests.length === 0 ? (
						<Text c="dimmed">{t("common.dash")}</Text>
					) : (
						user.requests.map((request) => (
							<Anchor
								key={request.id}
								component={Link}
								to={`/applications/${request.id}`}
							>
								{request.type} · {t(`status.${request.status}`)} ·{" "}
								{new Date(request.createdAt).toLocaleString("uk-UA")}
							</Anchor>
						))
					)}
				</Stack>
			</Paper>

			<Modal
				opened={Boolean(toggle)}
				onClose={() => setToggle(null)}
				title={
					toggle?.isActive
						? t("users.disableDiscount")
						: t("users.enableDiscount")
				}
			>
				<Stack>
					<Text size="sm">{toggle?.title}</Text>
					<Textarea
						label={
							toggle?.isActive
								? t("users.disableReason")
								: t("users.enableNote")
						}
						required={Boolean(toggle?.isActive)}
						value={reason}
						onChange={(event) => setReason(event.currentTarget.value)}
					/>
					<Group justify="flex-end">
						<Button variant="default" onClick={() => setToggle(null)}>
							{t("common.cancel")}
						</Button>
						<Button
							loading={stateMutation.isPending}
							disabled={Boolean(toggle?.isActive) && reason.trim().length === 0}
							onClick={() => stateMutation.mutate()}
						>
							{t("common.save")}
						</Button>
					</Group>
				</Stack>
			</Modal>
		</Stack>
	);
}
